/**
 * Minimal structural shape shared by every federated two-leg operation-check
 * entry (`UGCOrderOperationCheck`, `CommentOperationCheck`, ...): the
 * operation enum member plus its `authorization` and `validation` legs. The
 * legs stay optional/nullable so partial and legacy check shapes remain
 * assignable; the predicate treats them as denials.
 */
export type OperationCheck<Operation extends string> = {
  readonly operation: Operation;
  readonly authorization?: Readonly<{ passes?: boolean | null }> | null;
  readonly validation?: Readonly<{ passes?: boolean | null }> | null;
};

/**
 * Fail-closed two-leg operation predicate: true only when a check for
 * `operation` is present and BOTH legs explicitly pass — `authorization`
 * (the viewer holds the operation's action at the resource) and `validation`
 * (the resource's state admits the operation). A missing list, a missing
 * check, or an unresolved leg denies.
 */
export const operationCheckPasses = <Operation extends string>(
  checks: readonly OperationCheck<Operation>[] | null | undefined,
  operation: Operation,
): boolean => {
  const check = checks?.find((entry) => entry.operation === operation);
  return check?.authorization?.passes === true && check?.validation?.passes === true;
};

/**
 * A check whose validation leg discloses its clearable obstacles (fleet rule
 * P3): `blockers` lists them in the vocabulary's order and stays empty when
 * nothing the caller can change would admit the operation.
 */
export type BlockingOperationCheck<
  Operation extends string,
  Blocker,
> = OperationCheck<Operation> & {
  readonly blockers?: readonly Blocker[] | null;
};

/**
 * The fleet's three readings of a two-leg check with blockers
 * (docs/viewer-operation-checks/SCOPE.md, P3): `admitted` shows the gesture
 * enabled, `blocked` shows it disabled with the first blocker's sentence, and
 * `refused` hides it.
 */
export type OperationCheckVerdict<Blocker> =
  | { readonly verdict: "admitted" }
  | { readonly verdict: "blocked"; readonly blocker: Blocker }
  | { readonly verdict: "refused" };

/**
 * The verdict for `operation`: admitted exactly when `operationCheckPasses`
 * is; blocked when authorization passes, validation does not and a blocker
 * is named; refused otherwise, failing closed as the predicate does.
 * Authorization is read first, so a viewer who may not act learns no
 * obstacle from the check.
 */
export const operationCheckVerdict = <Operation extends string, Blocker>(
  checks: readonly BlockingOperationCheck<Operation, Blocker>[] | null | undefined,
  operation: Operation,
): OperationCheckVerdict<Blocker> => {
  const check = checks?.find((entry) => entry.operation === operation);
  if (check?.authorization?.passes !== true) {
    return { verdict: "refused" };
  }
  if (check.validation?.passes === true) {
    return { verdict: "admitted" };
  }
  const blocker = check.blockers?.[0];
  return blocker === undefined ? { verdict: "refused" } : { verdict: "blocked", blocker };
};

/**
 * Anything that carries a federated `viewerOperationChecks` list — an
 * organizational unit, a revision, … — as the admission guard sees it.
 */
type OperationCheckedEntity = {
  readonly viewerOperationChecks?: readonly OperationCheck<string>[] | null;
};

/** The operation vocabulary an entity's own check list speaks. */
type OperationOf<Entity extends OperationCheckedEntity> = NonNullable<
  Entity["viewerOperationChecks"]
>[number]["operation"];

/**
 * Admission form of the predicate, for route and page gates: returns the
 * entity when it arrived AND its check for `operation` passes on both legs,
 * otherwise `undefined` — so `const unit = gateByViewerOperation(ou, Read);
 * if (!unit) throw 403;` both decides and narrows. An absent entity admits
 * nobody, exactly as a missing check or an unresolved leg does
 * (`operationCheckPasses`): this is the one home for the fail-closed
 * admission posture, the `gateByViewerAction` twin for the two-leg surface.
 */
export const gateByViewerOperation = <Entity extends OperationCheckedEntity>(
  entity: Entity | null | undefined,
  operation: OperationOf<Entity>,
): Entity | undefined =>
  entity != null && operationCheckPasses(entity.viewerOperationChecks, operation)
    ? entity
    : undefined;
